// OverLayWrapper.tsx
"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
  useDragControls,
  PanInfo,
} from "framer-motion";

type SnapPoint = number | `${number}vh` | `${number}%`;

type OverlayWrapperProps = {
  /** Call this to request closing (parent should unmount the component). */
  onClose: () => void;

  /** Snap points for the sheet height (e.g. ['35vh','70vh','92vh'] or [320,560]). */
  snapPoints: SnapPoint[];

  /** Which snap index to open at. Defaults to the last index. */
  initialSnap?: number;

  /** If current height < this, close on drag end. Default: 50% of smallest snap. */
  closeThresholdPx?: number;

  /** Whether tapping the backdrop dismisses the sheet. Default: true. */
  dismissOnOverlay?: boolean;

  className?: string;
  handleClassName?: string;
  children: React.ReactNode;
  "aria-label"?: string;

  /** When false, disables drag entirely and hides the handle by default. */
  draggable?: boolean; // default: true

  /** Force show/hide of handle (defaults to follow `draggable`). */
  showHandle?: boolean;
};

/* ---------------- helpers ---------------- */
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function nearestIndex(values: number[], target: number) {
  let best = 0,
    dist = Infinity;
  for (let i = 0; i < values.length; i++) {
    const d = Math.abs(values[i] - target);
    if (d < dist) {
      best = i;
      dist = d;
    }
  }
  return best;
}
function toPx(sp: SnapPoint, H: number): number {
  if (typeof sp === "number") return sp;
  if (sp.endsWith("vh")) return (parseFloat(sp) / 100) * H;
  if (sp.endsWith("%")) return (parseFloat(sp) / 100) * H;
  return parseFloat(sp) || 0;
}
function usePortalRoot(id = "drag-sheet-root") {
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    let root = document.getElementById(id);
    if (!root) {
      root = document.createElement("div");
      root.id = id;
      document.body.appendChild(root);
    }
    setEl(root);
  }, [id]);
  return el;
}
function useBodyLock(locked: boolean) {
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    if (locked) {
      document.body.style.overflow = "hidden";
      document.documentElement.classList.add("overflow-hidden");
    }
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.classList.remove("overflow-hidden");
    };
  }, [locked]);
}
function useViewportHeight() {
  const [h, setH] = React.useState(0);
  React.useLayoutEffect(() => {
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    const update = () => setH(Math.round(vv?.height ?? window.innerHeight));
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return h;
}

/* ---------------- component ---------------- */
export default function OverlayWrapper({
  onClose,
  snapPoints,
  initialSnap,
  closeThresholdPx,
  dismissOnOverlay = true,
  children,
  className,
  handleClassName,
  draggable = true,
  showHandle,
  ...aria
}: OverlayWrapperProps) {
  const portal = usePortalRoot();

  // Lock scroll while mounted (including during exit animation)
  useBodyLock(true);

  // Ready gate for SSR and to get a stable viewport height
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  const H = useViewportHeight();
  const ready = mounted && H > 0;

  // Motion values / controls
  const y = useMotionValue(0);
  const controls = useAnimation();
  const sheetHeight = useTransform(y, (val) => Math.max(0, H - val));
  const HANDLE_H = (showHandle ?? draggable) ? 44 : 0;
  const dragControls = useDragControls();
  const lastCommittedY = React.useRef(0);

  React.useEffect(() => {
    const unsub = y.on("change", (val) => (lastCommittedY.current = val));
    return () => unsub();
  }, [y]);

  // Snap points (px)
  const snapPx = React.useMemo(() => {
    if (!ready) return [] as number[];
    const px = snapPoints
      .map((sp) => toPx(sp, H))
      .filter((n) => n > 0)
      .sort((a, b) => a - b);
    return px.length ? px : [Math.round(0.8 * H)]; // fallback ~80vh
  }, [ready, H, snapPoints]);

  const initialIndex = React.useMemo(
    () =>
      clamp(
        initialSnap ?? snapPoints.length - 1,
        0,
        Math.max(0, snapPoints.length - 1)
      ),
    [initialSnap, snapPoints.length]
  );

  const maxSnap = snapPx[snapPx.length - 1] || 0;
  const targetYForSnap = React.useCallback(
    (snap: number) => clamp(H - snap, 0, H),
    [H]
  );

  // Ensure we're visually closed before first paint, then animate in to initial snap
  const hasInitializedRef = React.useRef(false);
  React.useLayoutEffect(() => {
    if (!ready || hasInitializedRef.current) return;
    const closedY = H;
    y.set(closedY);
    lastCommittedY.current = closedY;
    hasInitializedRef.current = true;
  }, [ready, H, y]);

  const animateToSnap = React.useCallback(
    async (idx: number) => {
      if (!ready) return;
      const snap = snapPx[idx] ?? snapPx[snapPx.length - 1] ?? 0;
      const targetY = targetYForSnap(snap);
      await controls.start({
        y: targetY,
        transition: { type: "spring", stiffness: 420, damping: 40 },
      });
    },
    [ready, controls, snapPx, targetYForSnap]
  );

  // On mount (once we know snap points), open to the initial snap
  React.useEffect(() => {
    if (!ready || !snapPx.length) return;
    animateToSnap(clamp(initialIndex, 0, snapPx.length - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, snapPx.length]);

  // Backdrop click
  const onBackdropClick = React.useCallback(() => {
    if (dismissOnOverlay) onClose();
  }, [dismissOnOverlay, onClose]);

  // Drag behavior — handle-only; allow dragging beyond lowest snap to fully close.
  const onDrag = React.useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!draggable || !ready || !snapPx.length) return;
      const minY = targetYForSnap(snapPx[snapPx.length - 1]); // top-most
      const maxY = H; // allow dragging past the lowest snap to closed
      const next = clamp(lastCommittedY.current + info.delta.y, minY, maxY);
      y.set(next);
    },
    [draggable, ready, H, snapPx, targetYForSnap, y]
  );

  const onDragEnd = React.useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!draggable || !ready || !snapPx.length) return;
      const currentY = lastCommittedY.current;
      const currentHeight = H - currentY;
      const downwardFlick =
        info.velocity.y > 1200 && Math.abs(info.offset.y) > 20;
      const threshold = closeThresholdPx ?? snapPx[0] * 0.5;

      if (downwardFlick || currentHeight < threshold) {
        onClose(); // parent will unmount; exit anim handles slide down
        return;
      }
      animateToSnap(nearestIndex(snapPx, currentHeight));
    },
    [draggable, ready, H, snapPx, closeThresholdPx, onClose, animateToSnap]
  );

  // Backdrop opacity follows openness
  const overlayOpacity = useTransform(y, (val) => {
    if (!ready || !H || !maxSnap) return 0;
    const h = H - val;
    const p = clamp(h / maxSnap, 0, 1);
    return 0.4 * p;
  });

  // Build content (exit animations included)
  const content = (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <motion.button
        type="button"
        onClick={onBackdropClick}
        aria-label="Close"
        className="absolute inset-0 w-full h-full bg-black"
        style={{ opacity: overlayOpacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Surface */}
      <motion.div
        role="dialog"
        aria-modal="true"
        {...aria}
        className="fixed inset-0 flex items-end pointer-events-none max-w-2xl mx-auto"
        initial={false}
        animate="open"
        exit="exit"
      >
        <motion.div
          drag={draggable ? "y" : false}
          dragControls={dragControls}
          dragListener={false} // only drag from the handle
          dragElastic={0.12}
          dragMomentum={false}
          onDrag={draggable ? onDrag : undefined}
          onDragEnd={draggable ? onDragEnd : undefined}
          animate={controls}
          initial={{ y: H, }}
          // During presence exit, slide to closed and fade
          exit={{ y: H, transition: { duration: 0.18 } }}
          style={{
            y,
            willChange: "transform",
            height: sheetHeight,
            minHeight: "100%",
            ["--vvh" as any]: `${H || 0}px`,
            ["--handle-h" as any]: `${HANDLE_H}px`,
          }}
          className={`pointer-events-auto overflow-clip bg-white relative w-full rounded-t-2xl shadow-2xl border-t border-slate-200 ${className ?? ""
            }`}
        >
          {(showHandle ?? draggable) && (
            <div
              className={`absolute z-1000 left-[calc(50%-80px)] flex top-2 justify-center h-12 w-40 cursor-grab active:cursor-grabbing ${handleClassName ?? ""
                }`}
              style={{ touchAction: "none" }}
              onPointerDown={(e) => {
                if (draggable) dragControls.start(e);
              }}
            >
              <div className="h-1 w-10 rounded-full bg-slate-300" />
            </div>
          )}

          <div
            className="overflow-y-auto overscroll-contain"
            style={{
              height:
                "calc(100% - var(--handle-h) - env(safe-area-inset-bottom))",
            }}
          >
            {children}
            <div className="h-3" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );

  if (!ready) return null;
  return portal ? createPortal(content, portal) : content;
}
